/**
 * pmbok8CourseConfig.js
 * Constants for the PMBOK 8 Contact Hours standalone product.
 *
 * Aligned to the PMP Certification Exam Content Outline (ECO) - July 2026 Update.
 * Verified domain weights and task structure from the 2026 ECO:
 *   - People: 33% (8 tasks)
 *   - Process: 41% (10 tasks)
 *   - Business Environment: 26% (8 tasks)
 *
 * Total: 26 modules (one per ECO task) = 2,100 minutes = 35 contact hours.
 */

// Product Identity
export const PMBOK8_PRODUCT_ID = "pmbok8-contact-hours";
export const PMBOK8_PRODUCT_TITLE = "PMBOK® 8 Contact Hours (July 2026 Exam)";
export const PMBOK8_PRODUCT_SHORT_TITLE = "PMBOK 8 Contact Hours";
export const PMBOK8_PRICE = 249; // was 349 until Nov 4 2026; server: functions/stripe/premiumSaleConfig.js PMBOK8_COURSE
export const PMBOK8_CATEGORY_GROUP = "pmbok8-contact-hours";
export const PMBOK8_COURSE_IMAGE =
  "https://firebasestorage.googleapis.com/v0/b/thescope360.firebasestorage.app/o/productsAndServices-Images%2Fpmbok8-cover.png?alt=media";

// Payment (PayPal only for one-time contact-hours purchases)
export const PMBOK8_PAYMENT_PROVIDER = "paypal";

// Domain Definitions (ECO 2026 weights and task counts, verified)
// `foundations` is Module 0 orientation (counted, but not an exam domain -
// pmiWeight "Orientation" and it is filtered out of the 3-domain exam cards).
export const PMBOK8_DOMAINS = {
  foundations: {
    key: "foundations",
    title: "Foundations",
    description: "Orientation: PMI, the PMP, and your career",
    totalMinutes: 60,
    standardModules: 1,
    reviewModules: 0,
    color: "teal",
    pmiWeight: "Orientation",
    ecoTaskCount: 0,
  },
  people: {
    key: "people",
    title: "People",
    description: "Leading and managing the project team",
    totalMinutes: 690,
    standardModules: 8,
    reviewModules: 1,
    color: "yellow",
    pmiWeight: "33%",
    ecoTaskCount: 8,
  },
  process: {
    key: "process",
    title: "Process",
    description: "Managing the technical aspects of the project",
    totalMinutes: 860,
    standardModules: 10,
    reviewModules: 1,
    color: "cyan",
    pmiWeight: "41%",
    ecoTaskCount: 10,
  },
  businessEnvironment: {
    key: "businessEnvironment",
    title: "Business Environment",
    description: "Connection between projects, strategy, and organizational change",
    totalMinutes: 550,
    standardModules: 8,
    reviewModules: 1,
    color: "purple",
    pmiWeight: "26%",
    ecoTaskCount: 8,
  },
};

// Module Time Templates
// Standard People/Process module (80 min): intro 5 + A 20 + B 20 + C 20 + quiz 15
export const STANDARD_MODULE_TEMPLATE = {
  introVideo: 5,
  lessonA: 20,
  lessonB: 20,
  lessonC: 20,
  quiz: 15,
  total: 80,
};

// Business Environment modules run shorter (65 min) to match 26% domain weight
export const BIZENV_MODULE_TEMPLATE = {
  introVideo: 5,
  lessonA: 17,
  lessonB: 17,
  lessonC: 16,
  quiz: 10,
  total: 65,
};

export const REVIEW_MODULE_TEMPLATES = {
  people: { review: 35, quiz: 15, total: 50 },
  process: { review: 40, quiz: 20, total: 60 },
  businessEnvironment: { review: 20, quiz: 10, total: 30 },
};

// Course length vs certified hours.
// The course is 2,160 min: 2,100 min of ECO-aligned project management lessons (26 tasks + reviews)
// plus the 60-min Module 0 orientation. PMI's July 2026 ECO counts only the hours spent on project
// management, so the certificate counts the 2,100 min (35 hrs) and the orientation is not counted.
export const TOTAL_CONTACT_MINUTES = 2160; // whole course, for progress bars
export const PM_CONTACT_MINUTES = 2100; // counted toward the 35-hour requirement
export const TOTAL_CONTACT_HOURS = 35; // certified project management hours

// Domain Hours (for certificate display). Foundations is orientation and is not counted.
export const DOMAIN_HOURS = {
  foundations: 0,
  people: 11.5,
  process: 14.3,
  businessEnvironment: 9.2,
};

// Quiz Defaults
export const PMBOK8_QUIZ_DEFAULTS = {
  passingScore: 70,
  maxAttempts: 3,
  // Contact-hours fix F (Nov 2026): a module quiz can be submitted only after
  // 45 seconds per question (e.g. 7.5 minutes for 10 questions), counted while
  // the page is visible. The server checks the same rule
  // (QUIZ_SECONDS_PER_QUESTION in functions/pmbok8/pmbok8Credit.js).
  minSecondsPerQuestion: 45,
};

// "Are you still there?" idle check (owner OK Nov 4 2026). While the Reading,
// the exam scenarios or a module quiz is open, no input, scroll or touch for
// promptAfterSeconds shows a friendly prompt. Timers stop from the last
// activity until the learner answers, and that idle time is reported to the
// server, which leaves it out of credited time and records it in the time log.
// Slides and intro videos are not checked while narration or video plays
// (playing media counts as activity).
export const PMBOK8_IDLE_CHECK = {
  enabled: true,
  promptAfterSeconds: 2 * 60, // owner set 2 minutes Nov 4 2026
  steps: ["reading", "scenarios", "moduleQuiz"],
  activityEvents: ["pointerdown", "pointermove", "keydown", "wheel", "scroll", "touchstart"],
};

// Final Exam Config
// July 2026 PMP exam: 180 questions, 240 minutes, ~40% predictive / ~60% adaptive + hybrid
export const PMBOK8_FINAL_EXAM = {
  totalQuestions: 180,
  passingScore: 75,
  allottedMinutes: 240,
  predictivePortion: 0.4,
  adaptiveHybridPortion: 0.6,
  // No attempt limit for practice; students may retake as many times as needed
};

// Video Watch Threshold
export const VIDEO_WATCH_THRESHOLD = 0.75;

// Certificate Requirements
// The 35 project management hours plus the final exam (the ECO requires self-paced courses to
// include an end-of-course assessment; owner decision Oct 26 2026: taking it is required).
export const PMBOK8_CERTIFICATE_REQUIREMENTS = {
  completedMinutes: PM_CONTACT_MINUTES,
  requiresFinalExam: true,
  instructorName: "T. Laketia Jones, MBA, PMP",
  instructorTitle: "Lead Instructor",
  pmiDisclaimer:
    "PMI, PMP, and PMBOK are registered marks of the Project Management Institute, Inc. This course is not endorsed by or affiliated with PMI.",
  ecoAlignment: "Aligned to the PMP Certification Exam Content Outline - July 2026 Update",
};

// PDF Slide Storage
export const PMBOK8_SLIDES_STORAGE_PREFIX = "pmbok8-slides";

/**
 * Firebase Storage path for a lesson's PDF slides.
 * @param {string} lessonId e.g. "people-m1-a"
 * @returns {string} e.g. "pmbok8-slides/people-m1-a.pdf"
 */
export function getPmbok8SlidePath(lessonId) {
  return `${PMBOK8_SLIDES_STORAGE_PREFIX}/${lessonId}.pdf`;
}

// Progress Doc Path
export const PMBOK8_PROGRESS_COLLECTION = "courseProgress";
export const PMBOK8_PROGRESS_DOC_ID = PMBOK8_PRODUCT_ID;

export const getPMBOK8ProgressPath = (userId) =>
  `users/${userId}/${PMBOK8_PROGRESS_COLLECTION}/${PMBOK8_PROGRESS_DOC_ID}`;

// Utility Functions

export function getLetterGrade(score) {
  if (score >= 93) return "A";
  if (score >= 90) return "A-";
  if (score >= 87) return "B+";
  if (score >= 83) return "B";
  if (score >= 80) return "B-";
  if (score >= 77) return "C+";
  if (score >= 73) return "C";
  if (score >= 70) return "C-";
  if (score >= 67) return "D+";
  if (score >= 60) return "D";
  return "F";
}

export function getGradeColor(letterGrade) {
  if (letterGrade.startsWith("A")) return "text-emerald-400";
  if (letterGrade.startsWith("B")) return "text-blue-400";
  if (letterGrade.startsWith("C")) return "text-yellow-400";
  if (letterGrade.startsWith("D")) return "text-orange-400";
  return "text-red-400";
}

export function getDomainProgress(completedMinutes, domainKey) {
  const domain = PMBOK8_DOMAINS[domainKey];
  if (!domain) return 0;
  return Math.min(100, Math.round((completedMinutes / domain.totalMinutes) * 100));
}
